import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Users, 
  Package, 
  Share2, 
  TrendingUp, 
  Clock, 
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { API } from '../../App';

const AdminCoachSubscriptionsPage = () => {
  const [overview, setOverview] = useState(null);
  const [bulkPurchases, setBulkPurchases] = useState([]);
  const [referralCodes, setReferralCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSection, setExpandedSection] = useState('bulk');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, bulkRes, referralRes] = await Promise.all([
        API.get('/admin/coach-subscriptions/overview'),
        API.get('/admin/coach-subscriptions/bulk-purchases'),
        API.get('/admin/coach-subscriptions/referral-codes')
      ]);
      setOverview(overviewRes.data);
      setBulkPurchases(bulkRes.data);
      setReferralCodes(referralRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2C5530]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-coach-subscriptions-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">Coach Subscriptions</h1>
        <p className="text-[#57534E]">Track bulk seat purchases and referral code usage across all coaches.</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {/* Total Revenue */}
        <div className="bg-[#2C5530] text-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-8 h-8 text-white/70" />
          </div>
          <p className="text-white/70 text-sm mb-1">Total Coach Revenue</p>
          <p className="font-['Playfair_Display'] text-3xl">
            {formatCurrency(overview?.totals?.total_coach_revenue || 0)}
          </p>
        </div>

        {/* Students Onboarded */}
        <div className="bg-white border border-[#E7E5E4] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-8 h-8 text-[#2C5530]" />
          </div>
          <p className="text-[#737373] text-sm mb-1">Students Onboarded</p>
          <p className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">
            {overview?.totals?.total_students_onboarded || 0}
          </p>
        </div>

        {/* Bulk Seats Sold */}
        <div className="bg-white border border-[#E7E5E4] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-8 h-8 text-[#BC562B]" />
          </div>
          <p className="text-[#737373] text-sm mb-1">Bulk Seats Sold</p>
          <p className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">
            {overview?.bulk_purchases?.total_seats_sold || 0}
          </p>
          <p className="text-xs text-[#737373] mt-1">
            {overview?.bulk_purchases?.seats_claimed || 0} claimed, {overview?.bulk_purchases?.seats_pending || 0} pending
          </p>
        </div>

        {/* Referral Uses */}
        <div className="bg-white border border-[#E7E5E4] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Share2 className="w-8 h-8 text-[#2C5530]" />
          </div>
          <p className="text-[#737373] text-sm mb-1">Referral Code Uses</p>
          <p className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">
            {overview?.referral_codes?.total_uses || 0}
          </p>
          <p className="text-xs text-[#737373] mt-1">
            {overview?.referral_codes?.active_codes || 0} active codes
          </p>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {/* Bulk Purchase Revenue */}
        <div className="bg-white border border-[#E7E5E4] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
              <Package className="w-5 h-5 text-[#BC562B]" />
              Bulk Purchase Revenue
            </h3>
            <span className="text-2xl font-bold text-[#2C5530]">
              {formatCurrency(overview?.bulk_purchases?.total_revenue || 0)}
            </span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#57534E]">Total Purchases</span>
              <span className="font-medium">{overview?.bulk_purchases?.total_purchases || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#57534E]">Active Purchases</span>
              <span className="font-medium">{overview?.bulk_purchases?.active_purchases || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#57534E]">Coaches Participating</span>
              <span className="font-medium">{overview?.bulk_purchases?.coaches_participating || 0}</span>
            </div>
          </div>
        </div>

        {/* Referral Revenue */}
        <div className="bg-white border border-[#E7E5E4] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#2C5530]" />
              Referral Code Revenue
            </h3>
            <span className="text-2xl font-bold text-[#2C5530]">
              {formatCurrency(overview?.referral_codes?.estimated_revenue || 0)}
            </span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#57534E]">Total Codes</span>
              <span className="font-medium">{overview?.referral_codes?.total_codes || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#57534E]">Total Uses</span>
              <span className="font-medium">{overview?.referral_codes?.total_uses || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#57534E]">Coaches with Conversions</span>
              <span className="font-medium">{overview?.referral_codes?.coaches_with_conversions || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Purchases Table */}
      <div className="bg-white border border-[#E7E5E4] mb-6">
        <button
          onClick={() => setExpandedSection(expandedSection === 'bulk' ? '' : 'bulk')}
          className="w-full p-4 flex items-center justify-between bg-[#F2F0E9] border-b border-[#E7E5E4]"
        >
          <h2 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Package className="w-5 h-5 text-[#BC562B]" />
            Bulk Purchases ({bulkPurchases.length})
          </h2>
          {expandedSection === 'bulk' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSection === 'bulk' && (
          <div className="overflow-x-auto">
            {bulkPurchases.length > 0 ? (
              <table className="w-full">
                <thead className="bg-[#FAFAF9] text-left">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Coach</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Seats</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Amount</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Purchased</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Expires</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E7E5E4]">
                  {bulkPurchases.map((purchase) => (
                    <tr key={purchase.id} className="hover:bg-[#FAFAF9]">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{purchase.coach_name}</p>
                          <p className="text-xs text-[#737373]">{purchase.coach_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">{purchase.used_seats}</span>
                        <span className="text-[#737373]">/{purchase.total_seats}</span>
                        <span className="text-xs text-[#737373] ml-1">used</span>
                      </td>
                      <td className="px-4 py-3 font-medium text-[#2C5530]">
                        {formatCurrency(purchase.total_paid)}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#57534E]">
                        {formatDate(purchase.purchased_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#57534E]">
                        {formatDate(purchase.expires_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                          purchase.status === 'active' 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {purchase.status === 'active' ? (
                            <><CheckCircle className="w-3 h-3" /> Active</>
                          ) : (
                            <><Clock className="w-3 h-3" /> {purchase.status}</>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-[#737373]">
                No bulk purchases yet
              </div>
            )}
          </div>
        )}
      </div>

      {/* Referral Codes Table */}
      <div className="bg-white border border-[#E7E5E4]">
        <button
          onClick={() => setExpandedSection(expandedSection === 'referral' ? '' : 'referral')}
          className="w-full p-4 flex items-center justify-between bg-[#F2F0E9] border-b border-[#E7E5E4]"
        >
          <h2 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#2C5530]" />
            Referral Codes ({referralCodes.length})
          </h2>
          {expandedSection === 'referral' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSection === 'referral' && (
          <div className="overflow-x-auto">
            {referralCodes.length > 0 ? (
              <table className="w-full">
                <thead className="bg-[#FAFAF9] text-left">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Coach</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Code</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Uses</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Est. Revenue</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Created</th>
                    <th className="px-4 py-3 text-sm font-semibold text-[#57534E]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E7E5E4]">
                  {referralCodes.map((code) => (
                    <tr key={code.id} className="hover:bg-[#FAFAF9]">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{code.coach_name}</p>
                          <p className="text-xs text-[#737373]">{code.coach_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-[#2C5530] bg-[#F2F0E9] px-2 py-1">
                          {code.code.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-[#1A1A1A]">{code.usage_count}</span>
                      </td>
                      <td className="px-4 py-3 font-medium text-[#2C5530]">
                        {formatCurrency(code.estimated_revenue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#57534E]">
                        {formatDate(code.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                          code.is_active 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {code.is_active ? (
                            <><CheckCircle className="w-3 h-3" /> Active</>
                          ) : (
                            'Inactive'
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-[#737373]">
                No referral codes yet
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoachSubscriptionsPage;
